/**
 * BankSelector - Seletor de banco com busca integrada
 * 
 * Componente que lista bancos brasileiros regulamentados pelo BACEN
 * com filtro condicional baseado no tipo de conta selecionado.
 * 
 * - tipo_conta = corrente/poupanca: exibe todos os bancos
 * - tipo_conta = pagamento: exibe apenas Asaas
 * 
 * Utiliza padrão Popover + Command (mesmo do CrmInboxFilter)
 * 
 * @author Sistema
 * @version 1.0.0
 * RELEASE: 2026-03-14 v1.0.0 - Criação do componente
 */

import React, { useState, useMemo } from 'react';
import { Check, ChevronDown, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { BANCOS_BRASIL, type BancoBrasil } from '@/data/bancos-brasil';

/**
 * Props do componente BankSelector
 * @param tipoConta - Tipo de conta selecionado (corrente, poupanca, pagamento)
 * @param value - Código do banco selecionado
 * @param onChange - Callback ao selecionar banco (codigo, nome)
 * @param disabled - Se o seletor está desabilitado
 */
interface BankSelectorProps {
  tipoConta: string;
  value: string;
  onChange: (codigo: string, nome: string) => void;
  disabled?: boolean;
}

/**
 * Componente seletor de banco com busca
 * Filtra bancos conforme tipo de conta selecionado
 */
export function BankSelector({
  tipoConta,
  value,
  onChange,
  disabled = false,
}: BankSelectorProps) {
  const [open, setOpen] = useState(false);

  /**
   * Lista de bancos filtrada conforme tipo de conta
   * - pagamento: somente bancos com flag isPagamento (Asaas)
   * - corrente/poupanca: todos os bancos (exceto os exclusivos de pagamento)
   */
  const bancosFiltrados = useMemo<BancoBrasil[]>(() => {
    if (tipoConta === 'pagamento') {
      return BANCOS_BRASIL.filter(b => b.isPagamento);
    }
    // Para corrente/poupança, mostrar todos os bancos
    return BANCOS_BRASIL;
  }, [tipoConta]);

  // Banco selecionado atualmente
  const bancoSelecionado = BANCOS_BRASIL.find(b => b.codigo === value);

  /** Handler de seleção de banco */
  const handleSelect = (banco: BancoBrasil) => {
    onChange(banco.codigo, banco.nome);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || !tipoConta}
          className="w-full justify-between font-normal h-10"
        >
          {bancoSelecionado ? (
            <span className="truncate">
              {bancoSelecionado.codigo} - {bancoSelecionado.nome}
            </span>
          ) : (
            <span className="text-muted-foreground">
              {!tipoConta ? 'Selecione o tipo de conta primeiro' : 'Selecione o banco'}
            </span>
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar banco por nome ou código..." />
          <CommandList>
            <CommandEmpty>Nenhum banco encontrado.</CommandEmpty>
            <CommandGroup heading="Bancos">
              {bancosFiltrados.map(banco => (
                <CommandItem
                  key={banco.codigo}
                  value={`${banco.codigo} ${banco.nome}`}
                  onSelect={() => handleSelect(banco)}
                  className="cursor-pointer"
                >
                  <div
                    className={cn(
                      "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                      value === banco.codigo
                        ? "border-primary bg-primary"
                        : "border-muted"
                    )}
                  >
                    {value === banco.codigo && (
                      <Check className="h-2.5 w-2.5 text-primary-foreground" />
                    )}
                  </div>
                  <Building2 className="h-4 w-4 mr-2 opacity-50" />
                  <span className="font-mono text-xs mr-2 text-muted-foreground">{banco.codigo}</span>
                  <span className="truncate">{banco.nome}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
